import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  DropdownItem,
  DropdownMenu,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
  UncontrolledDropdown,
  DropdownToggle,
} from "reactstrap";
import classnames from "classnames";
import BreadCrumb from "components/common/BreadCrumb";
import { Link, useParams, useSearchParams } from "react-router-dom";
import StatusBadge from "components/common/StatusBadge";
import { DateOnly, DateTime } from "components/common/Date";
import PageLoader from "components/common/PageLoader";
import { toast } from "react-toastify";
import { Quotation } from "features/Quotations/types/quotation.types";
import {
  downloadQuotationPdf,
  getQuotation,
} from "features/Quotations/services/QuotationService";
import {
  QuotationPdfFormat,
  QuotationStatusMap,
  quotationWorkflowFlags,
} from "features/Quotations/constants/quotation.constants";
import { withLoadingToast } from "utils/withLoadingToast";
import { useAccess } from "features/Authentication/hooks/useAccess";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";
import QuotationWorkflowModals from "features/Quotations/components/QuotationWorkflowModals";
import { useQuotationWorkflow } from "features/Quotations/hooks/useQuotationWorkflow";
import {
  AnySourceType,
  SOURCE_TYPE_URL_MAP,
} from "constants/source-type.constants";
import { getDocumentTitle } from "utils/helpers";
import { TAB_MATERIALS_PRICING, TAB_OVERVIEW } from "./constants";
import { quotationItemsSubtotal } from "features/Quotations/utils/quotationItem.utils";
import QuotationOverviewTab from "./QuotationOverviewTab";
import QuotationMaterialsPricingTab from "./QuotationMaterialsPricingTab";

function QuotationOverview() {
  document.title = getDocumentTitle("Quotation overview");
  const { can } = useAccess();
  const canUpdate = can(Permissions.QUOTATION.UPDATE);
  const canReject = can(Permissions.QUOTATION.REJECT);
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [record, setRecord] = useState<Quotation | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [pdfExporting, setPdfExporting] = useState(false);

  const [subTotal, setSubTotal] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const numericId = Number(id);

    if (!Number.isFinite(numericId)) {
      setLoading(false);
      setLoadError(true);
      return;
    }

    const fetchRecord = async () => {
      setLoading(true);
      setLoadError(false);
      try {
        const response = await getQuotation(numericId);
        if (cancelled) return;
        if (response.success && response.data) {
          setRecord(response.data);
        } else {
          setRecord(null);
          setLoadError(true);
        }
      } catch (err) {
        console.warn(err);
        if (!cancelled) {
          setRecord(null);
          setLoadError(true);
          toast.error("Could not load quotation");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void fetchRecord();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const reloadQuotation = useCallback(async () => {
    const numericId = Number(id);
    if (!Number.isFinite(numericId)) return;
    try {
      const response = await getQuotation(numericId);
      if (response.success && response.data) {
        setRecord(response.data);
      }
    } catch {
      toast.error("Could not refresh quotation");
    }
  }, [id]);

  const workflow = useQuotationWorkflow({
    onSuccess: reloadQuotation,
    contextQuotation: record,
  });

  useEffect(() => {
    if (!record) {
      setSubTotal(0);
      return;
    }

    setSubTotal(quotationItemsSubtotal(record.items));
  }, [record]);

  const sourcePath = record?.sourceType
    ? SOURCE_TYPE_URL_MAP[record.sourceType as AnySourceType]
    : "";

  const headerTitle = useMemo(() => {
    if (!record) return "—";
    const t = record.title?.trim();
    return t || record.code || "—";
  }, [record]);

  const headerCode =
    record?.code && record.code !== headerTitle ? record.code : null;

  const activeTab = (() => {
    const tab = searchParams.get("tab");
    if (tab === TAB_MATERIALS_PRICING) return TAB_MATERIALS_PRICING;
    return TAB_OVERVIEW;
  })();

  const updateTab = (tab: string) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("tab", tab);
    setSearchParams(nextParams);
  };

  const handleExportPdf = async (format: QuotationPdfFormat) => {
    if (!record) return;
    setPdfExporting(true);
    try {
      await withLoadingToast(
        format === QuotationPdfFormat.BOQ
          ? "Preparing BOQ PDF…"
          : "Preparing quotation PDF…",
        () => downloadQuotationPdf(record.id, record.code, format),
        format === QuotationPdfFormat.BOQ
          ? "BOQ PDF downloaded"
          : "Quotation PDF downloaded",
        "Could not download quotation PDF",
        (err) =>
          err instanceof Error
            ? err.message
            : "Could not download quotation PDF",
      );
    } finally {
      setPdfExporting(false);
    }
  };

  const wf = record ? quotationWorkflowFlags(record.status) : null;

  if (loading) {
    return <PageLoader message="Loading quotation…" />;
  }

  if (loadError || !record) {
    return (
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title="Quotation overview"
            pageTitle="Quotations"
            items={[{ label: "Quotations", path: "/quotations" }]}
          />
          <p className="text-muted">This quotation could not be loaded.</p>
          <Link to="/quotations" className="btn btn-light btn-sm">
            Back to quotations
          </Link>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title="Quotation overview"
          pageTitle="Quotations"
          items={[{ label: "Quotations", path: "/quotations" }]}
        />

        <QuotationWorkflowModals workflow={workflow} />

        <Row>
          <Col lg={12}>
            <Card className="mt-n4 mx-n4">
              <div className="bg-light-subtle">
                <CardBody className="pb-0 px-4">
                  <Row className="mb-3 align-items-start">
                    <div className="col-md">
                      <h4 className="fw-bold">{headerTitle}</h4>
                      {headerCode ? (
                        <p className="text-muted mb-1">
                          <span className="fw-medium">{headerCode}</span>
                        </p>
                      ) : null}
                      <div className="hstack gap-3 flex-wrap">
                        <div className="d-flex align-items-center gap-2">
                          <span className="text-muted">Status:</span>
                          <StatusBadge
                            value={record.status}
                            map={QuotationStatusMap}
                          />
                        </div>
                        <div className="vr d-none d-sm-block" />
                        <div className="text-muted">
                          Valid until:{" "}
                          <span className="text-body fw-medium">
                            <DateOnly date={record.validUntil} />
                          </span>
                        </div>
                        {record.created_at ? (
                          <>
                            <div className="vr d-none d-sm-block" />
                            <div className="text-muted">
                              Created:{" "}
                              <span className="fw-medium">
                                <DateTime date={record.created_at} />
                              </span>
                            </div>
                          </>
                        ) : null}
                      </div>
                    </div>
                    <div className="col-md-auto mt-3 mt-md-0">
                      <div className="hstack gap-2 flex-wrap justify-content-md-end">
                        {/* {canSend && wf && (wf.canSend || wf.canResend) ? (
                          <Button
                            type="button"
                            color="primary"
                            size="sm"
                            title={
                              wf.canSend
                                ? "Email PDF to client"
                                : "Re-email PDF to client (status unchanged)"
                            }
                            onClick={() =>
                              wf.canSend
                                ? workflow.openSendConfirm()
                                : workflow.openResendConfirm()
                            }
                          >
                            <i
                              className={`${
                                wf.canSend
                                  ? "ri-send-plane-line"
                                  : "ri-mail-send-line"
                              } align-bottom me-1`}
                            />
                            {wf.canSend ? "Send" : "Resend"}
                          </Button>
                        ) : null} */}
                        <UncontrolledDropdown>
                          <DropdownToggle
                            type="button"
                            className="btn btn-light btn-sm border"
                            caret
                            disabled={pdfExporting}
                          >
                            <i className="ri-file-pdf-line align-bottom me-1" />
                            {pdfExporting ? "Exporting…" : "Export PDF"}
                          </DropdownToggle>
                          <DropdownMenu end>
                            <DropdownItem
                              onClick={() =>
                                void handleExportPdf(QuotationPdfFormat.NORMAL)
                              }
                              disabled={pdfExporting}
                            >
                              <i className="ri-file-pdf-line align-bottom me-2 text-muted" />
                              Standard PDF
                            </DropdownItem>
                            <DropdownItem
                              onClick={() =>
                                void handleExportPdf(QuotationPdfFormat.BOQ)
                              }
                              disabled={pdfExporting}
                            >
                              <i className="ri-file-list-3-line align-bottom me-2 text-muted" />
                              BOQ PDF
                            </DropdownItem>
                          </DropdownMenu>
                        </UncontrolledDropdown>
                        <Link
                          to={`/quotations/edit/${record.id}`}
                          className={`btn btn-light btn-sm border ${!wf?.canEdit ? "disabled opacity-50 pe-none" : ""}`}
                          title={
                            !wf?.canEdit
                              ? "Only draft quotations can be edited"
                              : undefined
                          }
                          onClick={(e) => {
                            if (!wf?.canEdit) e.preventDefault();
                          }}
                        >
                          <i className="ri-pencil-line align-bottom me-1" />
                          Edit
                        </Link>
                        {wf && canUpdate && wf.canAccept ? (
                          <button
                            type="button"
                            className="btn btn-success btn-sm btn-label"
                            onClick={() => workflow.openAcceptModal()}
                          >
                            <i className="ri-check-double-line label-icon align-middle fs-14" />
                            Accept
                          </button>
                        ) : null}
                        {wf && (canUpdate || canReject) ? (
                          <UncontrolledDropdown>
                            <DropdownToggle
                              type="button"
                              className="btn btn-light btn-sm border"
                            >
                              <i className="ri-more-2-fill align-middle" />
                            </DropdownToggle>
                            <DropdownMenu end>
                              {canUpdate ? (
                                <DropdownItem
                                  className="text-danger"
                                  disabled={!wf.canCancel}
                                  title={
                                    !wf.canCancel
                                      ? "Only draft or sent quotations can be cancelled"
                                      : undefined
                                  }
                                  onClick={() =>
                                    wf.canCancel
                                      ? workflow.openCancelModal()
                                      : undefined
                                  }
                                >
                                  <i className="ri-close-circle-line align-bottom me-2" />
                                  Cancel
                                </DropdownItem>
                              ) : null}
                              {canReject ? (
                                <DropdownItem
                                  className="text-danger"
                                  disabled={!wf.canReject}
                                  title={
                                    !wf.canReject
                                      ? "Only sent quotations can be rejected"
                                      : undefined
                                  }
                                  onClick={() =>
                                    wf.canReject
                                      ? workflow.openRejectModal()
                                      : undefined
                                  }
                                >
                                  <i className="ri-thumb-down-line align-bottom me-2" />
                                  Reject
                                </DropdownItem>
                              ) : null}
                            </DropdownMenu>
                          </UncontrolledDropdown>
                        ) : null}
                      </div>
                    </div>
                  </Row>

                  <Nav
                    className="nav-tabs-custom border-bottom-0"
                    role="tablist"
                  >
                    <NavItem>
                      <NavLink
                        className={classnames(
                          { active: activeTab === TAB_OVERVIEW },
                          "text-body fw-semibold",
                        )}
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          updateTab(TAB_OVERVIEW);
                        }}
                      >
                        Overview
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        className={classnames(
                          { active: activeTab === TAB_MATERIALS_PRICING },
                          "text-body fw-semibold",
                        )}
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          updateTab(TAB_MATERIALS_PRICING);
                        }}
                      >
                        Line Items
                      </NavLink>
                    </NavItem>
                  </Nav>
                </CardBody>
              </div>
            </Card>
          </Col>
        </Row>

        <Row>
          <Col lg={12}>
            <TabContent activeTab={activeTab} className="text-muted">
              <TabPane tabId={TAB_OVERVIEW}>
                <QuotationOverviewTab
                  record={record}
                  sourcePath={sourcePath ?? ""}
                />
              </TabPane>

              <TabPane tabId={TAB_MATERIALS_PRICING}>
                <QuotationMaterialsPricingTab
                  record={record}
                  subTotal={subTotal}
                />
              </TabPane>
            </TabContent>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

export default QuotationOverview;
